import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { captureTracking, getCheckoutUrl, getOrigem } from "../lib/tracking";
import { AudioPlayer } from "../components/AudioPlayer";
import { LeafBranch } from "../components/Botanical";
import {
  ArrowRight, Check, Headphones, BookOpen, ListChecks, ScanLine,
  Play, ChevronDown, Sparkles, ShieldCheck, BadgeCheck, HeartHandshake,
  Sunrise, Moon, Smile, Users, Heart, HandHeart, QrCode,
} from "lucide-react";

const COVER = `${process.env.PUBLIC_URL || ""}/assets/ebook-cover.jpg`;
const PREVIEWS = [
  { src: "/assets/preview-sumario.jpg", label: "Sumário" },
  { src: "/assets/preview-introducao.jpg", label: "Introdução" },
  { src: "/assets/preview-como-usar.jpg", label: "Como usar" },
  { src: "/assets/preview-capitulo.jpg", label: "Capítulos" },
  { src: "/assets/preview-exercicio.jpg", label: "Exercícios" },
];

const RECEIVE = [
  { icon: BookOpen, title: "E-book completo", text: "Um guia organizado para ajudar você a desenvolver seus momentos de oração." },
  { icon: Headphones, title: "50 orações narradas", text: "Quando não souber o que dizer, escolha uma oração, dê o play e acompanhe no seu tempo." },
  { icon: ListChecks, title: "Método prático + exercícios", text: "Pequenos exercícios para ajudar a transformar a oração em parte da sua rotina." },
  { icon: HeartHandshake, title: "Orações para cada momento", text: "Encontre palavras para agradecer, pedir força, buscar paz, orar pela família e muito mais." },
];

const HOW = [
  { n: 1, icon: BookOpen, title: "Abra o e-book", text: "Escolha a oração que combina com o momento que você está vivendo." },
  { n: 2, icon: ScanLine, title: "Escaneie o QR Code", text: "Use a câmera do celular. Não precisa instalar nenhum aplicativo." },
  { n: 3, icon: Play, title: "Dê o play", text: "Ouça a oração onde estiver e faça desse momento o seu encontro com Deus." },
];

const MOMENTS = [
  "Quando eu não souber o que dizer", "Para começar o dia", "Antes de dormir",
  "Quando eu estiver ansioso(a)", "Pela minha família", "Pelos meus filhos",
  "Quando eu pensar em desistir", "Para pedir perdão", "Quando eu estiver com medo",
  "Para agradecer", "Quando eu precisar de paz", "Quando eu só quiser conversar com Deus",
];

const BENEFITS = [
  { icon: BookOpen, title: "Quando você não souber o que dizer", text: "Abra uma oração e encontre palavras para começar." },
  { icon: Headphones, title: "Quando quiser apenas ouvir", text: "Dê o play e acompanhe uma das orações narradas." },
  { icon: ListChecks, title: "Quando quiser criar constância", text: "Use os exercícios para ajudar a incorporar a oração à sua rotina." },
];

const TRUST = [
  { icon: ShieldCheck, text: "Pagamento seguro pela Monetizze" },
  { icon: BadgeCheck, text: "Acesso digital imediato" },
  { icon: HeartHandshake, text: "Garantia de 7 dias" },
  { icon: Check, text: "Compra única, sem mensalidade" },
];

const FAQS = [
  { q: "O produto é físico?", a: "Não. O produto é 100% digital." },
  { q: "Como recebo o e-book?", a: "O acesso é disponibilizado digitalmente após a confirmação da compra." },
  { q: "Preciso instalar algum aplicativo?", a: "Não. O conteúdo pode ser acessado pelo navegador do celular." },
  { q: "Como funcionam as 50 orações em áudio?", a: "As orações possuem acesso por QR Code. Basta apontar a câmera do celular e acessar a oração correspondente." },
  { q: "Posso ouvir pelo celular?", a: "Sim." },
  { q: "O pagamento é mensal?", a: "Não. O pagamento de R$ 29,90 é único." },
  { q: "Quando recebo o material?", a: "Após a confirmação do pagamento." },
  { q: "Existe garantia?", a: "Sim. Garantia de 7 dias, conforme as condições aplicáveis à compra." },
];

function trackEvent(name, params = {}) {
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", name, params);
    }
  } catch (e) { /* noop */ }
}

function CTA({ href, testid, label, onClick, className = "" }) {
  return (
    <a
      href={href || "#"}
      target="_blank"
      rel="noreferrer"
      data-testid={testid}
      onClick={() => { trackEvent("CTA_Click", { cta: testid }); trackEvent("CheckoutOutbound", { cta: testid }); onClick && onClick(); }}
      className={`inline-flex items-center justify-center gap-2 rounded-full bg-[#C5A059] text-[#2A1D16] font-bold tracking-wide shadow-lg shadow-[#C5A059]/30 hover:bg-[#B08B44] hover:scale-[1.02] active:scale-[0.99] transition-all ${className}`}
    >
      {label} <ArrowRight className="w-5 h-5 shrink-0" />
    </a>
  );
}

const Eyebrow = ({ children }) => (
  <span className="font-cinzel uppercase tracking-[0.26em] text-[#C5A059] text-[11px] sm:text-xs">{children}</span>
);

export default function LandingTest() {
  const [prayer, setPrayer] = useState(null);
  const [price, setPrice] = useState("R$ 29,90");
  const [openFaq, setOpenFaq] = useState(0);
  const [checkoutUrl, setCheckoutUrl] = useState(getCheckoutUrl());
  const audioTracked = useRef(false);

  const audioSrc = `${api.defaults.baseURL}/prayers/1/audio`;

  useEffect(() => {
    captureTracking();
    setCheckoutUrl(getCheckoutUrl());
    trackEvent("LandingPageView", { origem: getOrigem(), page: "teste" });
    api.post("/track/visit", { origem: getOrigem() }).catch(() => {});
    api.get("/prayers/1").then((r) => setPrayer(r.data)).catch(() => {});
    api.get("/config").then((r) => r.data?.price && setPrice(r.data.price)).catch(() => {});
  }, []);

  const onAudioPlay = () => {
    if (!audioTracked.current) { audioTracked.current = true; trackEvent("AudioDemoPlay", { page: "teste" }); }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#3A2921] font-sans-body pb-24 sm:pb-0">
      {/* ===== 1. HERO ===== */}
      <section className="relative overflow-hidden">
        <div className="sun-rays pointer-events-none" aria-hidden="true" />
        <div className="sun-glow" aria-hidden="true" />
        <div className="max-w-6xl mx-auto px-5 pt-10 pb-14 sm:pt-14 sm:pb-20 grid md:grid-cols-2 gap-10 md:gap-12 items-center relative z-10">
          <div className="order-2 md:order-1 text-center md:text-left">
            <Eyebrow>E-book Devocional • 50 Orações em Áudio</Eyebrow>
            <h1 className="font-serif-display font-semibold leading-[1.1] text-3xl sm:text-4xl lg:text-5xl mt-4">
              Quer falar com Deus, mas muitas vezes não sabe nem por onde começar?
            </h1>
            <p className="text-[#6E5A4F] text-base sm:text-lg mt-5 leading-relaxed">
              Descubra uma forma simples de transformar a oração em um momento natural do seu dia — mesmo quando faltarem palavras.
            </p>
            <p className="font-serif-display italic text-[#3A2921] text-lg mt-4">
              <strong className="font-semibold not-italic">“Quando Faltam Palavras”</strong> — um guia prático com exercícios e 50 orações narradas para ajudar você a começar, criar constância e viver seus momentos de oração.
            </p>

            <ul className="mt-6 grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-[#4A362B] max-w-md mx-auto md:mx-0">
              {["E-book completo", "Exercícios práticos", "50 orações em áudio", "Acesso imediato"].map((f, i) => (
                <li key={i} className="flex items-center gap-2"><Check className="w-4 h-4 text-[#4C7A5A] shrink-0" strokeWidth={2.5} />{f}</li>
              ))}
            </ul>

            <div className="mt-7 flex flex-col items-center md:items-start gap-3">
              <div className="flex items-center gap-3 flex-wrap justify-center md:justify-start">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F4EbD8] text-[#8a6d2f] text-[11px] font-bold uppercase tracking-widest px-3 py-1 border border-[#E1CE9F]">
                  <Sparkles className="w-3.5 h-3.5" /> Oferta de lançamento
                </span>
                <div className="flex items-baseline gap-2">
                  <span className="font-serif-display text-4xl font-semibold">{price}</span>
                  <span className="text-xs text-[#8C7565] uppercase tracking-wide">pagamento único</span>
                </div>
              </div>
              <CTA href={checkoutUrl} testid="test-cta-hero" label="QUERO COMEÇAR AGORA" className="px-7 py-4 text-sm sm:text-base w-full sm:w-auto max-w-md" />
              <p className="flex flex-wrap items-center justify-center md:justify-start gap-x-2 text-xs text-[#8C7565]">
                <ShieldCheck className="w-3.5 h-3.5 text-[#4C7A5A]" /> Pagamento seguro <span className="text-[#D8CBB8]">•</span> Acesso imediato <span className="text-[#D8CBB8]">•</span> Garantia de 7 dias
              </p>
            </div>
          </div>

          <div className="order-1 md:order-2 flex justify-center">
            <div className="relative flex flex-col items-center">
              {/* Ramos decorativos à esquerda da capa */}
              <LeafBranch className="hidden sm:block pointer-events-none absolute -left-20 lg:-left-28 -top-6 w-28 lg:w-36 opacity-30 z-0 rotate-[-6deg]" />
              <LeafBranch className="hidden sm:block pointer-events-none absolute -left-16 lg:-left-24 top-40 w-32 lg:w-44 opacity-25 z-0 rotate-[8deg]" />

              <div className="relative z-10">
                {/* Selo redondo 50 orações em áudio */}
                <span className="absolute -top-6 -left-6 sm:-top-7 sm:-left-7 z-20 w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-[#3A2921] text-[#F7EFE0] border border-[#C5A059]/50 shadow-xl flex flex-col items-center justify-center text-center leading-tight rotate-[-4deg]">
                  <Headphones className="w-5 h-5 sm:w-6 sm:h-6 text-[#C5A059] mb-0.5" strokeWidth={1.8} />
                  <span className="font-serif-display text-2xl sm:text-3xl font-semibold leading-none">50</span>
                  <span className="text-[8px] sm:text-[9px] font-bold uppercase tracking-[0.15em] text-[#C5A059] mt-0.5">Orações<br />em áudio</span>
                </span>
                <div className="absolute -inset-5 bg-[#EADFCB] rounded-[2.5rem] blur-2xl opacity-60" />
                <img src={COVER} alt="E-book Quando Faltam Palavras" data-testid="test-hero-cover" className="relative w-[257px] sm:w-[331px] rounded-2xl shadow-2xl ring-1 ring-[#E5DCCF] rotate-[-2deg] hover:rotate-0 hover:scale-[1.03] transition-transform duration-500" />
              </div>

              {/* Selos organizados abaixo da capa (não cobrem o livro) */}
              <div className="relative z-10 mt-7 flex flex-wrap items-center justify-center gap-2.5 max-w-xs" data-testid="test-cover-trust">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F7F2E9] text-[#3A2921] shadow-sm border border-[#E5DCCF] px-3 py-1.5 text-xs font-medium">
                  <BadgeCheck className="w-3.5 h-3.5 text-[#4C7A5A]" /> Acesso imediato
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F7F2E9] text-[#3A2921] shadow-sm border border-[#E5DCCF] px-3 py-1.5 text-xs font-medium">
                  <ShieldCheck className="w-3.5 h-3.5 text-[#4C7A5A]" /> Garantia de 7 dias
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 2. IDENTIFICAÇÃO EMOCIONAL ===== */}
      <section className="bg-[#F4EFE6] border-y border-[#E5DCCF] py-14 sm:py-18">
        <div className="max-w-3xl mx-auto px-5 text-center">
          <p className="font-serif-display italic text-2xl sm:text-3xl leading-snug text-[#3A2921]">
            “Deus não espera palavras perfeitas. Ele espera apenas um coração disposto a conversar com Ele.”
          </p>
          <p className="text-[#6E5A4F] mt-6 leading-relaxed max-w-2xl mx-auto">
            Talvez você queira orar, mas não saiba como começar. Talvez existam dias em que as palavras simplesmente não aparecem. <strong className="font-semibold text-[#3A2921]">“Quando Faltam Palavras”</strong> foi criado para acompanhar você nesses momentos e ajudar a tornar a oração uma parte mais natural da sua rotina.
          </p>
          <div className="flex flex-wrap justify-center gap-2 mt-8">
            {["Quando a ansiedade aperta", "Antes de dormir", "Ao começar o dia", "Pela família", "Pelos filhos", "Quando pensar em desistir", "Para agradecer", "Quando estiver com medo", "Quando precisar de paz"].map((t, i) => (
              <span key={i} className="text-sm rounded-full bg-[#FDFBF7] border border-[#E5DCCF] px-3.5 py-1.5 text-[#6E5A4F]">{t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 3. O QUE VOCÊ RECEBE ===== */}
      <section className="max-w-5xl mx-auto px-5 py-14 sm:py-18">
        <div className="text-center mb-10">
          <Eyebrow>Tudo o que está incluído</Eyebrow>
          <h2 className="font-serif-display text-3xl sm:text-4xl font-semibold mt-3">O que você recebe</h2>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          {RECEIVE.map((c, i) => (
            <div key={i} className="bg-[#FAF7F2] rounded-2xl border border-[#E5DCCF] p-6 flex gap-4 items-start hover:shadow-md transition-shadow">
              <span className="inline-flex w-12 h-12 rounded-full bg-[#F4EFE6] border border-[#E5DCCF] items-center justify-center shrink-0">
                <c.icon className="w-6 h-6 text-[#C5A059]" strokeWidth={1.6} />
              </span>
              <div>
                <h3 className="font-serif-display text-xl font-semibold">{c.title}</h3>
                <p className="text-sm text-[#6E5A4F] mt-1.5 leading-relaxed">{c.text}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="text-center mt-10 flex flex-col items-center gap-2">
          <CTA href={checkoutUrl} testid="test-cta-receive" label="QUERO TER ACESSO POR R$ 29,90" className="px-8 py-4 text-base w-full sm:w-auto" />
          <span className="text-xs text-[#8C7565]">Pagamento único • Acesso imediato</span>
        </div>
      </section>

      {/* ===== 4. DEMONSTRAÇÃO DO ÁUDIO ===== */}
      <section className="bg-[#2A1D16] text-[#F7EFE0] py-14 sm:py-18">
        <div className="max-w-3xl mx-auto px-5 text-center">
          <span className="font-cinzel uppercase tracking-[0.26em] text-[#D8B36A] text-[11px] sm:text-xs">Experimente antes de decidir</span>
          <h2 className="font-serif-display text-3xl sm:text-4xl font-semibold mt-3">Antes de decidir, ouça uma das orações</h2>
          <p className="text-[#EADFCB] mt-3 leading-relaxed">Dê o play. Respire fundo. Este é apenas um pequeno exemplo das 50 orações que acompanham o e-book.</p>
          <div className="mt-8 bg-[#F7F2E9] rounded-3xl p-4 sm:p-5 shadow-xl" onPointerDown={onAudioPlay}>
            <div className="text-[#8C7565] text-xs uppercase tracking-widest font-cinzel mb-3 px-1">Oração 01 — {prayer?.title || "Quando eu não souber o que dizer"}</div>
            <AudioPlayer src={audioSrc} hasAudio={!!prayer?.has_audio} />
          </div>
          <p className="text-[#EADFCB] mt-6">Gostou desse momento? No e-book você terá acesso a <strong className="text-[#F7EFE0]">50 orações</strong> para diferentes situações da vida.</p>
          <div className="mt-6"><CTA href={checkoutUrl} testid="test-cta-audio" label="QUERO ACESSAR AS 50 ORAÇÕES" className="px-8 py-4 text-base w-full sm:w-auto" /></div>
        </div>
      </section>

      {/* ===== 5. COMO FUNCIONA ===== */}
      <section className="max-w-5xl mx-auto px-5 py-14 sm:py-18 text-center">
        <Eyebrow>Simples no dia a dia</Eyebrow>
        <h2 className="font-serif-display text-3xl sm:text-4xl font-semibold mt-3">Simples de usar no seu dia a dia</h2>
        <div className="grid sm:grid-cols-3 gap-5 mt-10 text-left">
          {HOW.map((s) => (
            <div key={s.n} className="bg-[#FAF7F2] rounded-2xl border border-[#E5DCCF] p-6">
              <div className="flex items-center gap-3 mb-3">
                <span className="inline-flex w-10 h-10 rounded-full bg-[#3A2921] text-[#F7EFE0] items-center justify-center font-cinzel font-bold">{s.n}</span>
                <s.icon className="w-6 h-6 text-[#C5A059]" strokeWidth={1.6} />
              </div>
              <h3 className="font-serif-display text-xl font-semibold">{s.title}</h3>
              <p className="text-sm text-[#6E5A4F] mt-1.5 leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===== 6. VEJA POR DENTRO ===== */}
      <section className="bg-[#F4EFE6] border-y border-[#E5DCCF] py-14 sm:py-18">
        <div className="max-w-6xl mx-auto px-5 text-center">
          <Eyebrow>Espie por dentro</Eyebrow>
          <h2 className="font-serif-display text-3xl sm:text-4xl font-semibold mt-3">Veja por dentro o que você vai receber</h2>
          <p className="text-[#6E5A4F] mt-3 max-w-xl mx-auto">Um material criado para ser simples de usar, agradável de ler e fácil de incorporar à sua rotina.</p>
          <div className="flex gap-4 overflow-x-auto pb-4 mt-10 snap-x sm:justify-center">
            {PREVIEWS.map((p, i) => (
              <figure key={i} className="shrink-0 snap-center">
                <img src={p.src} alt={`Página: ${p.label}`} loading="lazy" className="w-36 sm:w-44 rounded-xl border border-[#E5DCCF] shadow-md bg-white" />
                <figcaption className="text-xs uppercase tracking-widest text-[#9C877A] mt-3">{p.label}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 7. UMA ORAÇÃO PARA CADA MOMENTO ===== */}
      <section className="max-w-4xl mx-auto px-5 py-14 sm:py-18 text-center">
        <Eyebrow>50 orações</Eyebrow>
        <h2 className="font-serif-display text-3xl sm:text-4xl font-semibold mt-3">Uma oração para cada momento</h2>
        <div className="grid sm:grid-cols-2 gap-3 mt-10 text-left">
          {MOMENTS.map((m, i) => (
            <div key={i} className="flex items-center gap-3 bg-[#FAF7F2] rounded-xl border border-[#E5DCCF] px-4 py-3">
              <span className="w-2 h-2 rounded-full bg-[#C5A059] shrink-0" />
              <span className="text-[#4A362B]">{m}</span>
            </div>
          ))}
        </div>
        <p className="font-serif-display text-2xl text-[#C5A059] mt-8">+ 38 outras orações</p>
        <p className="text-[#6E5A4F] mt-1">50 orações para acompanhar diferentes momentos da sua caminhada.</p>
      </section>

      {/* ===== 8. BENEFÍCIOS ===== */}
      <section className="bg-[#F4EFE6] border-y border-[#E5DCCF] py-14 sm:py-18">
        <div className="max-w-5xl mx-auto px-5 grid sm:grid-cols-3 gap-5">
          {BENEFITS.map((b, i) => (
            <div key={i} className="bg-[#FDFBF7] rounded-2xl border border-[#E5DCCF] p-6 text-center">
              <span className="inline-flex w-12 h-12 rounded-full bg-[#F4EFE6] border border-[#E5DCCF] items-center justify-center mb-3"><b.icon className="w-6 h-6 text-[#C5A059]" strokeWidth={1.6} /></span>
              <h3 className="font-serif-display text-lg font-semibold">{b.title}</h3>
              <p className="text-sm text-[#6E5A4F] mt-1.5 leading-relaxed">{b.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===== 9. CONFIANÇA ===== */}
      <section className="max-w-4xl mx-auto px-5 py-14 sm:py-18 text-center">
        <h2 className="font-serif-display text-2xl sm:text-3xl font-semibold">Uma compra tranquila e sem risco</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8">
          {TRUST.map((t, i) => (
            <div key={i} className="flex flex-col items-center gap-2 bg-[#FAF7F2] rounded-2xl border border-[#E5DCCF] p-5">
              <t.icon className="w-7 h-7 text-[#4C7A5A]" strokeWidth={1.6} />
              <span className="text-xs text-[#6E5A4F] leading-tight">{t.text}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ===== 10. SOBRE O AUTOR ===== */}
      <section className="bg-[#F4EFE6] border-y border-[#E5DCCF] py-14 sm:py-18">
        <div className="max-w-3xl mx-auto px-5 flex flex-col sm:flex-row items-center gap-8 text-center sm:text-left">
          <div data-testid="author-photo-placeholder" className="shrink-0 w-28 h-28 rounded-full bg-[#EADFCB] border border-[#D8C6A8] flex items-center justify-center">
            <span className="font-serif-display text-3xl text-[#8a6d2f]">FR</span>
          </div>
          <div>
            <Eyebrow>Sobre o autor</Eyebrow>
            <h2 className="font-serif-display text-2xl sm:text-3xl font-semibold mt-2">Por que nasceu “Quando Faltam Palavras”?</h2>
            <p className="text-[#6E5A4F] mt-3 leading-relaxed">
              Este projeto nasceu de uma ideia simples: muitas pessoas querem conversar com Deus, mas em determinados momentos não encontram palavras. “Quando Faltam Palavras” foi criado para oferecer um ponto de partida — com reflexões, exercícios e orações que podem acompanhar diferentes momentos da vida.
            </p>
            <p className="font-serif-display italic text-[#3A2921] mt-4">— Felipe Ramalho</p>
          </div>
        </div>
      </section>

      {/* ===== 11. OFERTA FINAL ===== */}
      <section className="max-w-3xl mx-auto px-5 py-14 sm:py-18">
        <div className="bg-[#2A1D16] text-[#F7EFE0] rounded-[2rem] p-8 sm:p-12 text-center relative overflow-hidden">
          <Sparkles className="absolute top-6 right-6 w-6 h-6 text-[#C5A059]/50" />
          <h2 className="font-serif-display text-3xl sm:text-4xl font-semibold">Comece hoje a criar seu momento de oração</h2>
          <p className="text-[#EADFCB] mt-3 max-w-md mx-auto">Tenha sempre por perto um guia para aqueles momentos em que você quer falar com Deus, mas as palavras não vêm.</p>
          <ul className="mt-7 space-y-2.5 max-w-xs mx-auto text-left">
            {["E-book “Quando Faltam Palavras”", "Método prático de oração", "Exercícios guiados", "50 orações narradas em áudio", "Acesso digital imediato"].map((f, i) => (
              <li key={i} className="flex items-center gap-3"><span className="inline-flex w-5 h-5 rounded-full bg-[#C5A059]/25 items-center justify-center shrink-0"><Check className="w-3 h-3 text-[#C5A059]" strokeWidth={3} /></span><span className="text-[#F3E9DB] text-sm">{f}</span></li>
            ))}
          </ul>
          <div className="mt-8 inline-flex flex-col items-center">
            <span className="text-[11px] font-bold uppercase tracking-widest text-[#D8B36A]">Oferta de lançamento</span>
            <span className="font-serif-display text-5xl font-semibold mt-1">{price}</span>
            <span className="text-xs text-[#EADFCB]/80 uppercase tracking-wide">pagamento único • sem mensalidade</span>
          </div>
          <div className="mt-7"><CTA href={checkoutUrl} testid="test-cta-final" label="QUERO COMEÇAR AGORA" className="px-9 py-4 text-base w-full sm:w-auto" /></div>
          <p className="text-xs text-[#EADFCB]/80 mt-4 leading-relaxed">🔒 Pagamento seguro pela Monetizze<br />Acesso imediato após a confirmação • Garantia de 7 dias</p>
        </div>
      </section>

      {/* ===== 12. GARANTIA ===== */}
      <section className="max-w-2xl mx-auto px-5 pb-14 sm:pb-18 text-center">
        <div className="bg-[#FAF7F2] rounded-2xl border border-[#E5DCCF] p-8 flex flex-col items-center">
          <ShieldCheck className="w-10 h-10 text-[#4C7A5A]" strokeWidth={1.5} />
          <h3 className="font-serif-display text-2xl font-semibold mt-3">Você tem 7 dias para conhecer o material</h3>
          <p className="text-[#6E5A4F] mt-2 leading-relaxed">Se dentro de 7 dias você sentir que não é para você, é só solicitar o reembolso conforme as condições da compra. Simples assim.</p>
        </div>
      </section>

      {/* ===== 13. FAQ ===== */}
      <section className="bg-[#F4EFE6] border-t border-[#E5DCCF] py-14 sm:py-18">
        <div className="max-w-2xl mx-auto px-5">
          <h2 className="font-serif-display text-3xl sm:text-4xl font-semibold text-center mb-10">Perguntas frequentes</h2>
          <div className="space-y-3">
            {FAQS.map((f, i) => (
              <div key={i} className="bg-[#FDFBF7] rounded-2xl border border-[#E5DCCF] overflow-hidden">
                <button data-testid={`test-faq-${i}`} onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left">
                  <span className="font-serif-display text-lg text-[#3A2921]">{f.q}</span>
                  <ChevronDown className={`w-5 h-5 text-[#C5A059] shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && <p className="px-5 pb-5 text-[#6E5A4F] leading-relaxed">{f.a}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="text-center py-8 text-xs text-[#9C877A]">Quando Faltam Palavras · Acesso digital · Pagamento único</footer>

      {/* ===== STICKY MOBILE CTA ===== */}
      <div className="fixed bottom-0 inset-x-0 z-40 sm:hidden bg-[#FDFBF7]/95 backdrop-blur border-t border-[#E5DCCF] px-4 py-3 shadow-[0_-4px_20px_rgba(58,41,33,0.10)]">
        <div className="flex items-center justify-between gap-3">
          <div className="leading-tight">
            <div className="font-serif-display text-lg font-semibold text-[#3A2921]">{price}</div>
            <div className="text-[10px] text-[#8C7565] uppercase tracking-wide">pagamento único</div>
          </div>
          <CTA href={checkoutUrl} testid="test-cta-sticky" label="QUERO COMEÇAR" className="px-5 py-3 text-sm flex-1 max-w-[220px]" />
        </div>
      </div>
    </div>
  );
}
